// src/pages/intranet/pages/MegaAdmin.jsx
import { useState, useEffect } from 'react';
import { Shield, Building, Users, Search, Edit, Plus, X, AlertTriangle } from 'lucide-react';

export default function MegaAdmin() {
  const [activeTab, setActiveTab] = useState('logias');
  const [logias, setLogias] = useState([]);
  const [usuariosGlobales, setUsuariosGlobales] = useState([]);
  
  // Estados para modales
  const [isModalNuevaLogiaOpen, setIsModalNuevaLogiaOpen] = useState(false);
  const [isModalEditarLogiaOpen, setIsModalEditarLogiaOpen] = useState(false);
  const [logiaSeleccionada, setLogiaSeleccionada] = useState(null);
  
  const [isModalNuevoUserOpen, setIsModalNuevoUserOpen] = useState(false);
  const [isModalEditarUserOpen, setIsModalEditarUserOpen] = useState(false);
  const [userSeleccionado, setUserSeleccionado] = useState(null);

  useEffect(() => {
    if (activeTab === 'logias') cargarLogias();
    if (activeTab === 'usuarios') {
      cargarUsuariosGlobales();
      cargarLogias(); // Necesitamos las logias para el selector al crear/editar usuarios
    }
  }, [activeTab]);

  const cargarLogias = async () => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/admin/logias');
      const data = await res.json();
      if (data.success) setLogias(data.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const cargarUsuariosGlobales = async () => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/admin/usuarios');
      const data = await res.json();
      if (data.success) setUsuariosGlobales(data.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // --- HANDLERS PARA LOGIAS ---
  const handleCrearLogia = async (formData) => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/admin/logias', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setIsModalNuevaLogiaOpen(false);
        cargarLogias();
      } else alert('Error: ' + data.error);
    } catch (error) {
      alert('Error de conexión.');
    }
  };

  const abrirEditarLogia = (logia) => {
    setLogiaSeleccionada(logia);
    setIsModalEditarLogiaOpen(true);
  };

  const handleEditarLogia = async (formData) => {
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/admin/logias/${logiaSeleccionada.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setIsModalEditarLogiaOpen(false);
        cargarLogias();
      } else alert('Error: ' + data.error);
    } catch (error) {
      alert('Error de conexión.');
    }
  };

  // --- HANDLERS PARA USUARIOS GLOBALES ---
  const handleCrearUsuario = async (formData) => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/usuarios/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setIsModalNuevoUserOpen(false);
        cargarUsuariosGlobales();
      } else alert('Error: ' + data.error);
    } catch (error) {
      alert('Error de conexión.');
    }
  };

  const abrirEditarUsuario = (usr) => {
    setUserSeleccionado(usr);
    setIsModalEditarUserOpen(true);
  };

  const handleEditarUsuario = async (formData) => {
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/admin/usuarios/${userSeleccionado.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setIsModalEditarUserOpen(false);
        cargarUsuariosGlobales();
      } else alert('Error: ' + data.error);
    } catch (error) {
      alert('Error de conexión.');
    }
  };

  return (
    <div className="flex flex-col gap-6 lg:gap-8 animate-fade-in relative">
      <div className="flex justify-between items-center bg-red-900/10 border border-red-900/30 p-6 shadow-lg">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-red-500 m-0 tracking-wide flex items-center gap-3">
            <Shield size={32} /> Panel de Gran Oriente (Mega Admin)
          </h1>
          <p className="text-gray-400 font-light text-sm mt-2 m-0">Control total de la infraestructura, sedes y miembros a nivel nacional.</p>
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 shadow-2xl overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
        
        <div className="flex border-b border-white/10 overflow-x-auto hide-scrollbar relative z-10 bg-[#050505]/50">
          <button 
            className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 text-xs md:text-sm tracking-[0.2em] uppercase transition-all duration-300 font-semibold border-b-2 ${activeTab === 'logias' ? 'border-[#C5A059] text-[#C5A059] bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300'}`} 
            onClick={() => setActiveTab('logias')}
          >
            <Building size={16} /> Sedes y Logias
          </button>
          
          <button 
            className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 text-xs md:text-sm tracking-[0.2em] uppercase transition-all duration-300 font-semibold border-b-2 ${activeTab === 'usuarios' ? 'border-[#C5A059] text-[#C5A059] bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300'}`} 
            onClick={() => setActiveTab('usuarios')}
          >
            <Users size={16} /> Visión Global de HH:.
          </button>
        </div>

        <div className="p-6 md:p-10 relative z-10">
          
          {/* VISTA 1: LOGIAS */}
          {activeTab === 'logias' && (
            <div className="flex flex-col gap-6 animate-fade-in">
              <div className="flex justify-between items-center">
                <h3 className="font-serif text-2xl text-white m-0">Catálogo de Sedes Activas</h3>
                <button 
                  onClick={() => setIsModalNuevaLogiaOpen(true)}
                  className="flex items-center gap-2 px-6 py-3 text-xs tracking-widest uppercase font-bold bg-[#C5A059] text-[#050505] hover:bg-white transition-colors"
                >
                  <Plus size={16} /> Instanciar Nueva Logia
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {logias.map((logia) => (
                  <div key={logia.id} className="bg-[#050505] border border-white/5 p-6 shadow-lg hover:border-[#C5A059]/30 transition-colors">
                    <div className="flex justify-between items-start mb-4">
                      <span className="text-[#C5A059] font-serif text-3xl">N° {logia.numero_logia}</span>
                      <span className={`px-2 py-1 text-[9px] uppercase tracking-widest font-mono border ${logia.estado === 'activa' ? 'bg-green-900/20 text-green-400 border-green-800/50' : 'bg-red-900/20 text-red-400 border-red-800/50'}`}>
                        {logia.estado}
                      </span>
                    </div>
                    <h4 className="text-white text-lg font-medium tracking-wide m-0 mb-1">{logia.nombre_logia}</h4>
                    <p className="text-gray-500 text-xs font-mono uppercase tracking-widest mb-6">Valle de {logia.valle}</p>
                    
                    <div className="flex justify-between items-center pt-4 border-t border-white/10">
                      <span className="text-gray-400 text-[10px] tracking-widest uppercase">Admin. Sede</span>
                      <button onClick={() => abrirEditarLogia(logia)} className="text-gray-500 hover:text-[#C5A059] transition-colors"><Edit size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* VISTA 2: USUARIOS GLOBALES */}
          {activeTab === 'usuarios' && (
            <div className="flex flex-col gap-6 animate-fade-in">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                <div className="flex items-center bg-[#050505] border border-white/10 px-4 py-3 w-full md:w-96 gap-3">
                  <Search size={16} className="text-gray-500" />
                  <input type="text" placeholder="Buscar hermano por nombre, DNI o logia..." className="bg-transparent border-none outline-none text-white text-sm w-full font-light" />
                </div>
                
                <button 
                  onClick={() => setIsModalNuevoUserOpen(true)}
                  className="flex items-center justify-center gap-2 px-6 py-3 text-xs tracking-widest uppercase font-bold bg-[#C5A059] text-[#050505] hover:bg-white transition-colors w-full md:w-auto"
                >
                  <Plus size={16} /> Nuevo Hermano
                </button>
              </div>

              <div className="overflow-x-auto bg-[#050505] border border-white/5">
                <table className="w-full text-left border-collapse min-w-[1000px]">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Logia Origen</th>
                      <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Hermano</th>
                      <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">DNI</th>
                      <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Grado</th>
                      <th className="py-4 px-6 text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuariosGlobales.map((usr) => (
                      <tr key={usr.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-6 text-gray-300 text-xs font-mono uppercase tracking-widest">
                          {usr.nombre_logia ? `${usr.nombre_logia} N° ${usr.numero_logia}` : 'Sin Asignar'}
                        </td>
                        <td className="py-4 px-6 text-white text-sm font-medium">{usr.nombres} {usr.apellidos}</td>
                        <td className="py-4 px-6 text-gray-400 text-xs font-mono">{usr.dni}</td>
                        <td className="py-4 px-6 text-gray-400 text-xs tracking-widest uppercase">
                          <span className={`${usr.estado === 'inactivo' ? 'text-red-500 line-through' : ''}`}>{usr.grado}</span>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <button onClick={() => abrirEditarUsuario(usr)} className="text-gray-500 hover:text-white transition-colors p-2"><Edit size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>

      {isModalNuevaLogiaOpen && <LogiaModal onClose={() => setIsModalNuevaLogiaOpen(false)} onSave={handleCrearLogia} />}
      {isModalEditarLogiaOpen && <LogiaModal logiaData={logiaSeleccionada} onClose={() => setIsModalEditarLogiaOpen(false)} onSave={handleEditarLogia} />}
      
      {isModalNuevoUserOpen && <UsuarioGlobalModal logias={logias} onClose={() => setIsModalNuevoUserOpen(false)} onSave={handleCrearUsuario} />}
      {isModalEditarUserOpen && <UsuarioGlobalModal logias={logias} userData={userSeleccionado} onClose={() => setIsModalEditarUserOpen(false)} onSave={handleEditarUsuario} />}
    </div>
  );
}

// --- SUBCOMPONENTES MODALES ---

function LogiaModal({ logiaData, onClose, onSave }) {
  const [formData, setFormData] = useState({
    nombre_logia: logiaData?.nombre_logia || '',
    numero_logia: logiaData?.numero_logia || '',
    valle: logiaData?.valle || '',
    primer_venerable_maestro: logiaData?.primer_venerable_maestro || '',
    estado: logiaData?.estado || 'activa'
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-[#050505]/90 backdrop-blur-md flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 rounded-none w-full max-w-lg p-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50"></div>
        
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <h2 className="font-serif text-2xl text-white m-0">{logiaData ? 'Editar Sede' : 'Aperturar Nueva Sede'}</h2>
          <button onClick={onClose} className="bg-transparent border-none text-gray-500 hover:text-white transition-colors cursor-pointer p-1"><X size={20} /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Nombre de la Logia</label>
            <input type="text" name="nombre_logia" required value={formData.nombre_logia} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Número Asignado</label>
              <input type="number" name="numero_logia" required value={formData.numero_logia} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Valle (Ciudad/Región)</label>
              <input type="text" name="valle" required value={formData.valle} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
             <div className="flex flex-col gap-2">
               <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Venerable Maestro</label>
               <input type="text" name="primer_venerable_maestro" value={formData.primer_venerable_maestro} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
             </div>
             {logiaData && (
               <div className="flex flex-col gap-2">
                 <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Estado de Operación</label>
                 <select name="estado" value={formData.estado} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] appearance-none cursor-pointer">
                   <option value="activa">Activa</option>
                   <option value="inactiva">Inactiva (Clausurada)</option>
                 </select>
               </div>
             )}
          </div>
          
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors">Cancelar</button>
            <button type="submit" className="bg-[#C5A059] hover:bg-white text-[#050505] border-none px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors">Guardar Datos</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UsuarioGlobalModal({ logias, userData, onClose, onSave }) {
  const [formData, setFormData] = useState({
    logia_id: userData?.logia_id || '',
    dni: userData?.dni || '',
    nombres: userData?.nombres || '',
    apellidos: userData?.apellidos || '',
    email: userData?.email || '',
    telefono: userData?.telefono || '',
    grado: userData?.grado || 'APRENDIZ',
    estado: userData?.estado || 'activo',
    password: '' // Nuevo campo gestionado
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const generarPassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$*";
    let pass = "";
    for (let i = 0; i < 10; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password: pass });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-[#050505]/90 backdrop-blur-md flex justify-center items-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 rounded-none w-full max-w-lg p-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50"></div>
        
        <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
          <h2 className="font-serif text-2xl text-white m-0">{userData ? 'Editar Hermano' : 'Alta Global de Hermano'}</h2>
          <button onClick={onClose} className="bg-transparent border-none text-gray-500 hover:text-white transition-colors cursor-pointer p-1"><X size={20} /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Asignar a Sede (Logia)</label>
            <select name="logia_id" required value={formData.logia_id} onChange={handleChange} className="w-full bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] appearance-none cursor-pointer mt-1">
              <option value="">-- Seleccionar Logia --</option>
              {logias.map(logia => (
                <option key={logia.id} value={logia.id}>{logia.nombre_logia} N° {logia.numero_logia}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Documento (DNI)</label>
              <input type="text" name="dni" required value={formData.dni} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            </div>
            <div className="flex flex-col gap-2">
               <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Estado en la Logia</label>
               <select name="estado" value={formData.estado} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] appearance-none cursor-pointer">
                 <option value="activo">Activo Regular</option>
                 <option value="pendiente">Pendiente de Aprobación</option>
                 <option value="inactivo">Inactivo / En Sueños</option>
                 <option value="retirado">Retirado / Plancha de Quite</option>
               </select>
             </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Nombres</label>
              <input type="text" name="nombres" required value={formData.nombres} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Apellidos</label>
              <input type="text" name="apellidos" required value={formData.apellidos} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Correo Electrónico</label>
              <input type="email" name="email" required value={formData.email} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Grado Actual</label>
              <select name="grado" value={formData.grado} onChange={handleChange} className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] appearance-none cursor-pointer">
                <option value="APRENDIZ">Aprendiz</option>
                <option value="COMPAÑERO">Compañero</option>
                <option value="MAESTRO">Maestro</option>
                <option value="ADMIN">Mega Admin (Gran Oriente)</option>
              </select>
            </div>
          </div>

          {/* NUEVO: Campo de Contraseña Dinámica */}
          <div className="flex flex-col gap-2 mt-2">
            <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Contraseña de Acceso</label>
            <div className="flex gap-2">
              <input 
                type="text" 
                name="password" 
                placeholder={userData ? "Dejar en blanco para no modificar" : "Ingresa o genera una..."} 
                value={formData.password} 
                onChange={handleChange} 
                className="w-full bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059] font-mono" 
              />
              <button 
                type="button" 
                onClick={generarPassword} 
                className="bg-white/5 border border-white/10 hover:border-[#C5A059] text-gray-400 hover:text-[#C5A059] px-4 text-xs uppercase tracking-widest transition-colors whitespace-nowrap"
              >
                Generar
              </button>
            </div>
            {!userData && (
               <span className="text-gray-600 text-[9px] font-mono uppercase tracking-widest mt-1">Si se deja en blanco, la contraseña por defecto será el DNI.</span>
            )}
          </div>
          
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors">Cancelar</button>
            <button type="submit" className="bg-[#C5A059] hover:bg-white text-[#050505] border-none px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors">Guardar Usuario</button>
          </div>
        </form>
      </div>
    </div>
  );
}